import React from 'react';
import { getLocationFromString } from '@cp/ds/src/utils';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useEventCallback } from '@cp/ds/src/hooks/useEventCallback';
import { StyledDiv } from '@cp/ds/src/components/styled/styledDiv';
import { Typography } from '@cp/ds/src/components/typography';
import { Global } from '@emotion/react';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import { Skeleton } from '@cp/ds/src/components/skeleton';
import { Button } from '@cp/ds/src/components/button';
import { FullScreenExitIcon } from '@cp/ds/src/icons/fullScreenExit';
import { useCurrentCity } from '@cp/entities/location';
import { MapHandle, MapMarkerConfigId, MapProps } from './model';
import { loadDG } from './loader';
import {
  MAP_CLUSTER_GROUP_CLASSNAME,
  MAP_DEFAULT_ZOOM,
  MAP_FULLSCREEN_CONTROL_SELECTOR,
  MAP_MARKER_CLUSTER_ICON_SIZE,
  MAP_MARKER_CLUSTER_RADIUS_MAX,
  MAP_MARKER_ICON_ANCHOR,
  MAP_MARKER_ICON_SIZE,
  MAP_MARKER_POPUP_ANCHOR,
} from './const';

export const Map = React.forwardRef<MapHandle, MapProps>(
  (
    {
      zoom = MAP_DEFAULT_ZOOM,
      center,
      sx,
      onMapCreated: _onMapCreated,
      onMapDestroy: _onMapDestroy,
      onClose,
      zoomControl,
      fullscreenControl,
      markers,
      fallback,
    },
    ref,
  ) => {
    const { currentCity } = useCurrentCity({ allowEmptyValue: true });
    const theme = useTheme();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapRef = React.useRef<any>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const markersRef = React.useRef<{ mcg: any; md: Record<MapMarkerConfigId, any> }>();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const mapCreationOptions = React.useRef(omitBy({ zoomControl, fullscreenControl }, isUndefined));
    const onMapCreated = useEventCallback(_onMapCreated ?? (() => undefined));
    const onMapDestroy = useEventCallback(_onMapDestroy ?? (() => undefined));

    React.useImperativeHandle(ref, () => ({
      requestFullscreen: () => {
        const fullscreenButton = window.document.querySelector(MAP_FULLSCREEN_CONTROL_SELECTOR) as HTMLButtonElement | undefined;
        if (fullscreenButton) {
          fullscreenButton.click();
        }
      },
    }));

    React.useEffect(() => onMapDestroy, [onMapDestroy]);

    React.useEffect(() => {
      let isEffectInstanceActive = true;

      (async () => {
        await loadDG();

        if (!isEffectInstanceActive) {
          return;
        }

        const dg = window.DG;
        const cityCenter = getLocationFromString(center ?? currentCity?.centerPoint);

        if (mapRef.current) {
          if (cityCenter) {
            mapRef.current.panTo(cityCenter);
          }
        } else {
          mapRef.current = dg.map(containerRef.current, {
            center: cityCenter,
            zoom,
            // scrollWheelZoom: false,
            ...mapCreationOptions.current,
          });

          onMapCreated?.(mapRef.current);
        }

        return () => {
          isEffectInstanceActive = false;
        };
      })();
    }, [center, currentCity?.centerPoint, onMapCreated, zoom]);

    React.useEffect(() => {
      if (!markers?.length) {
        return;
      }

      const addMarkesToMap = () => {
        if (markersRef.current) {
          markersRef.current.mcg.removeFrom(mapRef.current);
          markersRef.current = undefined;
        }

        markersRef.current = {
          mcg: window.DG.markerClusterGroup({
            maxClusterRadius: MAP_MARKER_CLUSTER_RADIUS_MAX,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            iconCreateFunction: (cluster: any) => {
              return window.DG.divIcon({
                html: `<span>${cluster.getChildCount()}</span>`,
                className: MAP_CLUSTER_GROUP_CLASSNAME,
                iconSize: window.DG.point(MAP_MARKER_CLUSTER_ICON_SIZE, MAP_MARKER_CLUSTER_ICON_SIZE),
              });
            },
          }),
          md: {},
        };

        for (const { id, location: loc, iconUrl, popupContent, onClick } of markers) {
          const location = getLocationFromString(loc);

          const icon = iconUrl
            ? window.DG.icon({
                // className: 'map-mark-icon__default',
                iconUrl,
                iconRetinaUrl: iconUrl,
                iconSize: MAP_MARKER_ICON_SIZE,
                iconAnchor: MAP_MARKER_ICON_ANCHOR,
                popupAnchor: MAP_MARKER_POPUP_ANCHOR,
              })
            : undefined;

          const newMarker = window.DG.marker(
            location,
            omitBy(
              {
                icon,
              },
              isUndefined,
            ),
          )
            // https://leafletjs.com/reference.html#evented
            .on('click', onClick)
            .addTo(markersRef.current.mcg);

          if (popupContent) {
            newMarker.bindPopup(popupContent);
          }

          markersRef.current.md[id] = markersRef.current.mcg.getLayerId(newMarker);
        }

        markersRef.current.mcg.addTo(mapRef.current);
      };

      let isCurrentEffectInstanceActive = true;
      const checkMapLoadingIntervalInMs = 100;

      if (mapRef.current) {
        addMarkesToMap();
      } else {
        const intervalId = setInterval(() => {
          if (!mapRef.current && isCurrentEffectInstanceActive) {
            return;
          }

          clearInterval(intervalId);

          if (mapRef.current) {
            addMarkesToMap();
          }
        }, checkMapLoadingIntervalInMs);
      }

      return () => {
        isCurrentEffectInstanceActive = false;
      };
    }, [markers]);

    return (
      <StyledDiv ref={containerRef} sx={sx}>
        <Global
          styles={{
            [`.${MAP_CLUSTER_GROUP_CLASSNAME}`]: {
              display: 'flex !important',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 2,
              fontSize: '18px',
              fontWeight: 700,
              borderRadius: '50%',
              border: '4px solid',
              borderColor: theme.palette['brand gray'],
              color: theme.palette['brand gray'],
              backgroundColor: theme.palette.background.default,
            },
          }}
        />
        <StyledDiv sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {fallback ? (
            typeof fallback === 'string' ? (
              <Typography sx={{ textAlign: 'center' }}>{fallback}</Typography>
            ) : (
              fallback
            )
          ) : (
            <Skeleton sx={{ width: 1, height: 1 }} variant="rounded" />
          )}
        </StyledDiv>
        {onClose ? (
          <Button
            color="gray"
            onClick={onClose}
            size="small"
            square
            sx={{ backgroundColor: 'white', position: 'absolute', top: 20, right: 20, zIndex: 500 }}
            variant="secondary"
          >
            <FullScreenExitIcon />
          </Button>
        ) : null}
      </StyledDiv>
    );
  },
);
